import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getEquipmentById,
    getCategories,
    createEquipment,
    updateEquipment,
    uploadEquipmentImage,
    deleteEquipmentImage,
    supabaseServiceRole
} from '../../services/supabase';
import { Button } from '../../components/ui/Button';

const AdminEquipmentForm = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isEditMode = !!id;

    // Состояние формы
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category_id: '',
        status: 'available',
        daily_rate: '',
        image_url: '',
        quantity: '1',
    });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Запрашиваем оборудование для редактирования
    const {
        data: equipment,
        isLoading: equipmentLoading,
        error: equipmentError,
    } = useQuery({
        queryKey: ['equipment', id],
        queryFn: async () => {
            if (!id) return null;
            const { data, error } = await getEquipmentById(id);
            if (error) throw error;
            return data;
        },
        enabled: isEditMode,
    });

    // Получаем категории
    const {
        data: categories,
        isLoading: categoriesLoading,
        error: categoriesError,
    } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const { data, error } = await getCategories();
            if (error) throw error;
            return data;
        },
    });

    // Заполняем форму данными, если это режим редактирования
    useEffect(() => {
        if (equipment) {
            setFormData({
                name: equipment.name,
                description: equipment.description || '',
                category_id: equipment.category_id,
                status: equipment.status || 'available',
                daily_rate: equipment.daily_rate ? String(equipment.daily_rate) : '',
                image_url: equipment.image_url || '',
                quantity: equipment.quantity ? String(equipment.quantity) : '1',
            });

            if (equipment.image_url) {
                setImagePreview(equipment.image_url);
            }
        }
    }, [equipment]);

    // Мутация для создания оборудования
    const createEquipmentMutation = useMutation({
        mutationFn: async (data: any) => {
            return await createEquipment(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['equipment'] });
        },
    });

    // Мутация для обновления оборудования
    const updateEquipmentMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: any }) => {
            return await updateEquipment(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['equipment'] });
        },
    });

    // Обработчик изменения полей формы
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Очищаем ошибку валидации при изменении поля
        if (validationErrors[name]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // Обработчик выбора изображения
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // Валидация формы
    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!formData.name.trim()) {
            errors.name = 'Название обязательно для заполнения';
        }

        if (!formData.category_id) {
            errors.category_id = 'Выберите категорию';
        }

        if (formData.daily_rate && isNaN(Number(formData.daily_rate))) {
            errors.daily_rate = 'Цена должна быть числом';
        }

        // Проверяем валидность поля количества
        if (!formData.quantity || isNaN(Number(formData.quantity))) {
            errors.quantity = 'Количество должно быть числом';
        } else if (Number(formData.quantity) < 0) {
            errors.quantity = 'Количество не может быть отрицательным';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Обработчик отправки формы
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setIsUploading(true);

            // Подготавливаем данные
            const equipmentData: any = {
                ...formData,
                daily_rate: formData.daily_rate ? Number(formData.daily_rate) : null,
                quantity: formData.quantity ? Number(formData.quantity) : 1,
            };

            // При создании нового оборудования всегда устанавливаем статус "available"
            if (!isEditMode) {
                equipmentData.status = 'available';

                // Находим максимальное значение sort_order для этой категории
                try {
                    const { data: existingEquipment, error } = await supabaseServiceRole
                        .from('equipment')
                        .select('sort_order')
                        .eq('category_id', equipmentData.category_id)
                        .order('sort_order', { ascending: false })
                        .limit(1);

                    if (!error && existingEquipment && existingEquipment.length > 0) {
                        // Берем максимальное значение sort_order и увеличиваем на 1
                        const maxSortOrder = existingEquipment[0].sort_order || 0;
                        equipmentData.sort_order = maxSortOrder + 1;
                        console.log(`Установлен sort_order: ${equipmentData.sort_order} для нового оборудования`);
                    } else {
                        // Если нет оборудования в этой категории, устанавливаем sort_order = 1
                        equipmentData.sort_order = 1;
                        console.log(`Установлен sort_order: 1 для первого оборудования в категории`);
                    }
                } catch (sortError) {
                    console.error('Ошибка при определении порядка сортировки:', sortError);
                    // В случае ошибки устанавливаем значение по умолчанию
                    equipmentData.sort_order = 1000;
                }
            }

            let uploadedImageUrl = '';

            // Загружаем изображение, если выбрано
            if (imageFile) {
                try {
                    const fileName = `equipment_${Date.now()}_${imageFile.name.replace(/\s+/g, '_')}`;
                    const imageUrl = await uploadEquipmentImage(imageFile, fileName);
                    uploadedImageUrl = imageUrl;
                    equipmentData.image_url = imageUrl;
                } catch (imageError) {
                    console.error('Ошибка при загрузке изображения:', imageError);
                    // Если не удалось загрузить изображение, спрашиваем пользователя, хочет ли он продолжить без изображения
                    if (!window.confirm('Не удалось загрузить изображение. Сохранить оборудование без изображения?')) {
                        throw new Error(`Ошибка при загрузке изображения: ${imageError instanceof Error ? imageError.message : 'неизвестная ошибка'}`);
                    }
                    // Если пользователь согласен, продолжаем без изображения
                    equipmentData.image_url = '';
                }
            }

            // Создаем или обновляем оборудование
            try {
                if (isEditMode && id) {
                    const { error } = await updateEquipmentMutation.mutateAsync({ id, data: equipmentData });
                    if (error) {
                        throw new Error(`Ошибка обновления оборудования: ${String(error)}`);
                    }
                } else {
                    const { error } = await createEquipmentMutation.mutateAsync(equipmentData);
                    if (error) {
                        throw new Error(`Ошибка создания оборудования: ${String(error)}`);
                    }
                }

                // Если сохранение успешно, перенаправляем на список оборудования
                alert('Оборудование успешно сохранено!');
                navigate('/admin/equipment');
            } catch (saveError) {
                // Если загрузили изображение, но не смогли сохранить оборудование,
                // попробуем удалить изображение, чтобы не оставлять мусор
                if (uploadedImageUrl) {
                    try {
                        const path = uploadedImageUrl.split('/').pop();
                        if (path) {
                            await deleteEquipmentImage(path);
                        }
                    } catch (deleteError) {
                        console.error('Не удалось удалить загруженное изображение:', deleteError);
                    }
                }
                throw saveError;
            }
        } catch (error) {
            console.error('Ошибка при сохранении оборудования:', error);
            alert(`Произошла ошибка: ${error instanceof Error ? error.message : 'неизвестная ошибка'}`);
        } finally {
            setIsUploading(false);
        }
    };

    // Проверяем загрузку данных
    const isLoading = (isEditMode && equipmentLoading) || categoriesLoading;
    const isMutating = createEquipmentMutation.isPending || updateEquipmentMutation.isPending;
    const errors = equipmentError || categoriesError;

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <div className="w-10 h-10 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
            </div>
        );
    }

    if (errors) {
        return (
            <div className="bg-red-50 text-red-600 p-6 rounded-apple mb-8">
                Ошибка при загрузке данных: {(errors as Error).message}
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-8">
                {isEditMode ? 'Редактирование оборудования' : 'Добавление оборудования'}
            </h1>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 mb-8">
                {/* Название */}
                <div className="mb-6">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Название оборудования <span className="text-yellow-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 bg-white text-gray-900
                        ${validationErrors.name
                                ? 'border-red-300 focus:ring-red-200'
                                : 'border-gray-100 focus:ring-primary-100 focus:border-primary hover:border-gray-200'
                            }`}
                        placeholder="Введите название оборудования"
                    />
                    {validationErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                </div>

                {/* Описание */}
                <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Описание
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-100 rounded-lg transition-all duration-300 focus:ring-2 focus:ring-primary-100 focus:border-primary focus:outline-none bg-white text-gray-900 hover:border-gray-200"
                        placeholder="Введите описание оборудования"
                    />
                </div>

                {/* Категория */}
                <div className="mb-6">
                    <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                        Категория <span className="text-yellow-500">*</span>
                    </label>
                    <select
                        id="category_id"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 appearance-none bg-white text-gray-900
                        ${validationErrors.category_id
                                ? 'border-red-300 focus:ring-red-200'
                                : 'border-gray-100 focus:ring-primary-100 focus:border-primary hover:border-gray-200'
                            }`}
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23999' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 1rem center',
                            backgroundSize: '1rem'
                        }}
                    >
                        <option value="">Выберите категорию</option>
                        {categories?.map((category: any) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    {validationErrors.category_id && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.category_id}</p>
                    )}
                </div>

                {/* Стоимость аренды */}
                <div className="mb-6">
                    <label htmlFor="daily_rate" className="block text-sm font-medium text-gray-700 mb-2">
                        Стоимость аренды (₽/день)
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            id="daily_rate"
                            name="daily_rate"
                            value={formData.daily_rate}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 pr-10 bg-white text-gray-900
                            ${validationErrors.daily_rate
                                    ? 'border-red-300 focus:ring-red-200'
                                    : 'border-gray-100 focus:ring-primary-100 focus:border-primary hover:border-gray-200'
                                }`}
                            placeholder="Введите стоимость аренды"
                        />
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">₽</span>
                    </div>
                    {validationErrors.daily_rate && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.daily_rate}</p>
                    )}
                </div>

                {/* Количество */}
                <div className="mb-6">
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                        Количество единиц
                    </label>
                    <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        min="0"
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 bg-white text-gray-900
                        ${validationErrors.quantity
                                ? 'border-red-300 focus:ring-red-200'
                                : 'border-gray-100 focus:ring-primary-100 focus:border-primary hover:border-gray-200'
                            }`}
                        placeholder="Введите количество"
                    />
                    {validationErrors.quantity && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.quantity}</p>
                    )}
                </div>

                {/* Изображение */}
                <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Изображение оборудования
                    </label>

                    {imagePreview ? (
                        <div className="mb-4 relative group">
                            <img
                                src={imagePreview}
                                alt="Предпросмотр"
                                className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200 transition-all duration-300 shadow-sm group-hover:shadow-md"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-300 flex items-center justify-center">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImageFile(null);
                                        setImagePreview(null);
                                        setFormData((prev) => ({ ...prev, image_url: '' }));
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-2 bg-white rounded-full shadow-md text-red-500 hover:text-red-600 transition-all duration-300"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="mb-4 w-full max-w-md h-48 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 cursor-pointer hover:border-primary hover:bg-gray-50 transition-all duration-300">
                            <label className="cursor-pointer text-center p-4">
                                <div className="flex flex-col items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-sm text-gray-400">Выберите изображение</span>
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </label>
                        </div>
                    )}

                    <div className="flex items-center space-x-2">
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => document.getElementById('imageInput')?.click()}
                        >
                            {imagePreview ? 'Изменить изображение' : 'Выбрать изображение'}
                        </Button>
                        <input
                            id="imageInput"
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                        />
                    </div>
                </div>

                {/* Кнопки действий */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate('/admin/equipment')}
                    >
                        Отмена
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isMutating || isUploading}
                        disabled={isMutating || isUploading}
                    >
                        Сохранить
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default AdminEquipmentForm; 